/**
 * 付费弹窗 — 套餐选择 + 扫码支付
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { usePricingStore } from '../store/pricingStore';
import { useAuthStore } from '../store/authStore';
import { paymentApi } from '../services/paymentApi';
import { authApi } from '../services/authApi';
import { PlanInfo, OrderInfo } from '../types';

const POLL_INTERVAL = 2000;
const POLL_TIMEOUT = 5 * 60 * 1000;

const PricingModal: React.FC = () => {
  const { showPricingModal, closePricingModal } = usePricingStore();
  const { user, setAuth, token } = useAuthStore();
  const [plans, setPlans] = useState<PlanInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [order, setOrder] = useState<OrderInfo | null>(null);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (showPricingModal) {
      setLoading(true);
      setError('');
      setSelectedPlan(null);
      setOrder(null);
      paymentApi
        .getPlans()
        .then((res) => setPlans(res.data))
        .catch(() => setError('加载套餐失败'))
        .finally(() => setLoading(false));
    }
  }, [showPricingModal]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (orderId: number) => {
      const startTime = Date.now();
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const res = await paymentApi.getOrder(orderId);
          const o = res.data;
          if (o.status === 'paid') {
            stopPolling();
            const meRes = await authApi.getMe();
            setAuth(token!, meRes.data);
            closePricingModal();
          } else if (o.status === 'cancelled' || o.status === 'expired') {
            stopPolling();
            setOrder(o);
            setError('订单已取消或过期');
          } else if (Date.now() - startTime > POLL_TIMEOUT) {
            stopPolling();
            setError('支付超时，请重新下单');
          }
        } catch {
          // 忽略轮询错误
        }
      }, POLL_INTERVAL);
    },
    [token, setAuth, stopPolling, closePricingModal]
  );

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const handleSelectPlan = async (planId: string) => {
    setSelectedPlan(planId);
    setError('');
    setCreatingOrder(true);
    try {
      const res = await paymentApi.createOrder(planId);
      setOrder(res.data);
      startPolling(res.data.order_id);
    } catch (err: any) {
      setError(err.response?.data?.detail || '创建订单失败');
    } finally {
      setCreatingOrder(false);
    }
  };

  const handleBackToPlans = () => {
    stopPolling();
    setSelectedPlan(null);
    setOrder(null);
    setError('');
  };

  const isPlanMaxed = (plan: PlanInfo) =>
    plan.max_per_user != null && plan.max_per_user > 0 && (plan.user_purchased_count || 0) >= plan.max_per_user;

  const formatPrice = (price: number) =>
    (price / 100).toFixed(2).replace(/0+$/, '').replace(/\.$/, '');

  if (!showPricingModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* 遮罩 */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closePricingModal} />

      {/* 弹窗主体 */}
      <div className="relative card shadow-modal rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* 关闭按钮 */}
        <button
          onClick={closePricingModal}
          className="absolute top-4 right-4 w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-slate-600 hover:bg-slate-100 transition-colors z-10"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="p-8">
          {/* 标题 */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900">开通 VIP</h2>
            <p className="mt-2 text-sm text-slate-500">付费后解锁全部功能，AI 智能生成标书</p>
          </div>

          {/* 管理员提示 */}
          {isAdmin && (
            <div className="mb-6 p-4 bg-brand-50 border border-[#dbeafe] rounded-xl text-center">
              <p className="text-sm font-medium text-brand-700">管理员无需付费，可直接使用所有功能</p>
            </div>
          )}

          {/* 错误提示 */}
          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm text-center">
              {error}
            </div>
          )}

          {/* 加载 */}
          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052d9]" />
            </div>
          )}

          {/* 套餐选择 */}
          {!loading && !order && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {plans.map((plan) => {
                const maxed = isPlanMaxed(plan);
                return (
                <div
                  key={plan.id}
                  className={`bg-white rounded-xl border-2 p-6 text-center transition-all ${
                    maxed ? 'opacity-50 border-slate-200' :
                    selectedPlan === plan.plan_id
                      ? 'border-[#0052d9] shadow-[0_4px_16px_rgba(0,82,217,0.1)]'
                      : 'border-slate-200 hover:border-[#d0d5dd]'
                  }`}
                >
                  <h3 className="text-base font-bold text-slate-900">{plan.name}</h3>
                  <div className="mt-3">
                    <span className="text-3xl font-bold text-slate-900">¥{formatPrice(plan.price)}</span>
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    {plan.word_limit ? `${(plan.word_limit / 10000).toFixed(0)}万字 · ` : '' }{plan.description}
                  </p>
                  <p className={`mt-1 text-xs font-medium ${maxed ? 'text-red-500' : 'text-gray-400'}`}>
                    已购 {plan.user_purchased_count || 0} 次
                    {plan.max_per_user != null && plan.max_per_user > 0 && ` / 限 ${plan.max_per_user} 次`}
                  </p>
                  <button
                    onClick={() => handleSelectPlan(plan.plan_id)}
                    disabled={creatingOrder || maxed}
                    className={`mt-4 w-full py-2.5 px-4 rounded-xl text-sm font-semibold transition-all ${
                      maxed ? 'bg-gray-200 text-gray-400 cursor-not-allowed' :
                      creatingOrder && selectedPlan === plan.plan_id
                        ? 'bg-gray-400 cursor-not-allowed text-white'
                        : 'bg-brand-700 hover:bg-[#0042b3] text-white shadow-[0_4px_12px_rgba(0,82,217,0.2)] active:scale-[0.98]'
                    }`}
                  >
                    {maxed ? '已达上限' : creatingOrder && selectedPlan === plan.plan_id ? '创建中...' : '立即开通'}
                  </button>
                </div>
              )})}
            </div>
          )}

          {/* QR 支付 */}
          {order && order.status === 'pending' && (
            <div className="max-w-sm mx-auto text-center">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">微信扫码支付</h3>
              <div className="inline-block p-4 bg-white border border-slate-200 rounded-xl">
                <QRCodeSVG value={order.qr_code_url} size={200} />
              </div>
              <p className="mt-4 text-sm text-slate-600">
                金额：<span className="font-semibold text-slate-900">¥{formatPrice(order.amount)}</span>
              </p>
              <p className="mt-1 text-xs text-gray-400">请使用微信扫描二维码完成支付</p>
              <p className="mt-1 text-xs text-gray-400">支付成功后自动关闭弹窗</p>
              <button
                onClick={handleBackToPlans}
                className="mt-4 text-sm text-brand-700 hover:underline"
              >
                ← 返回选择其他套餐
              </button>
            </div>
          )}

          {/* 已取消/过期 */}
          {order && (order.status === 'cancelled' || order.status === 'expired') && (
            <div className="max-w-sm mx-auto text-center">
              <p className="text-slate-600 mb-4">订单已{order.status === 'cancelled' ? '取消' : '过期'}</p>
              <button
                onClick={handleBackToPlans}
                className="px-6 py-2.5 bg-brand-700 text-white rounded-xl hover:bg-[#0042b3] text-sm font-semibold"
              >
                重新选择
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingModal;
